import { REACTIONS } from '@minigame-race/contracts';
import { useEffect, useRef, useState } from 'react';
import { color } from '../theme.js';
import type { LiveReaction } from '../lib/useGame.js';
import { LottieEmote, preloadEmotes } from './LottieEmote.js';
import { Icon } from '../components/Icon.js';

/**
 * In-game emotes.
 *
 * Three exported pieces, so the trigger can live wherever it fits and the
 * floating display is shared:
 *  - `ReactionFloats`: the emotes that rise and fade in the side-margins. Yours
 *    (lime) on the left, opponent's (cyan) on the right. `pointer-events:none`,
 *    so it can never intercept a tap meant for the game.
 *  - `EmoteButton`: a small round button that rolls a column of emotes DOWN when
 *    tapped. Sits next to the "?" button so it is out of the play area and never
 *    over the race indicator. Same on desktop and mobile.
 *  - `EmoteRow`: a horizontal row of emotes for the between-games overlay, where
 *    there is room and the player is idle anyway.
 */

const COOLDOWN_MS = 500;

/** Shared cooldown so no surface can strobe emotes. */
function useFire(onSend: (r: string) => void): (r: string) => void {
  const last = useRef(0);
  return (r: string) => {
    const now = performance.now();
    if (now - last.current < COOLDOWN_MS) return;
    last.current = now;
    onSend(r);
  };
}

export function ReactionFloats({ reactions }: { reactions: readonly LiveReaction[] }) {
  return (
    <div style={{ position: 'absolute', inset: 0, zIndex: 13, pointerEvents: 'none', overflow: 'hidden' }} aria-hidden>
      {reactions.map((r, i) => (
        <span
          key={r.id}
          style={{
            position: 'absolute',
            bottom: 'clamp(40px,12cqmin,90px)',
            left: r.fromYou ? `calc(7% + ${(i % 3) * 26}px)` : undefined,
            right: r.fromYou ? undefined : `calc(7% + ${(i % 3) * 26}px)`,
            filter: `drop-shadow(0 0 10px ${r.fromYou ? 'rgba(166,255,46,.5)' : 'rgba(34,224,255,.5)'})`,
            animation: 'reactionFloat 1.8s ease-out forwards',
          }}
        >
          <LottieEmote reaction={r.reaction} size="clamp(34px,8cqmin,58px)" />
        </span>
      ))}
    </div>
  );
}

/** The trigger that rolls a column of emotes down. Place beside the "?" button. */
export function EmoteButton({ onSend }: { onSend: (reaction: string) => void }) {
  const [open, setOpen] = useState(false);
  const fire = useFire(onSend);

  // Auto-close if left open, so it never lingers over the board.
  useEffect(() => {
    if (!open) return;
    preloadEmotes(); // warm the Lottie cache the moment the picker opens
    const t = window.setTimeout(() => setOpen(false), 4000);
    return () => window.clearTimeout(t);
  }, [open]);

  const pick = (r: string): void => {
    fire(r);
    setOpen(false);
  };

  return (
    <div style={{ position: 'relative', zIndex: 15 }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? 'Close reactions' : 'Send a reaction'}
        title="React"
        style={{
          width: 'clamp(24px,4.4cqmin,34px)',
          height: 'clamp(24px,4.4cqmin,34px)',
          borderRadius: '50%',
          border: `1px solid ${open ? 'rgba(166,255,46,.5)' : 'rgba(166,255,46,.25)'}`,
          background: open ? 'rgba(166,255,46,.14)' : 'rgba(166,255,46,.06)',
          color: color.lime,
          cursor: 'pointer',
          display: 'grid',
          placeItems: 'center',
          padding: 0,
          touchAction: 'manipulation',
        }}
      >
        <Icon name={open ? 'x' : 'smile'} size="58%" />
      </button>

      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 'clamp(3px,1cqmin,6px)',
            padding: 'clamp(5px,1.2cqmin,8px)',
            borderRadius: 16,
            border: '1px solid rgba(166,255,46,.25)',
            background: 'linear-gradient(180deg,rgba(16,22,14,.97),rgba(9,13,8,.97))',
            boxShadow: '0 10px 28px rgba(0,0,0,.55)',
            animation: 'emoteRollDown .18s ease-out',
            transformOrigin: 'top right',
          }}
        >
          {REACTIONS.map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => pick(r)}
              aria-label={`React ${r}`}
              style={{
                width: 'clamp(34px,7cqmin,44px)',
                height: 'clamp(34px,7cqmin,44px)',
                borderRadius: '50%',
                border: 0,
                background: 'rgba(255,255,255,.04)',
                cursor: 'pointer',
                display: 'grid',
                placeItems: 'center',
                touchAction: 'manipulation',
              }}
            >
              <LottieEmote reaction={r} size="clamp(24px,5.4cqmin,34px)" loop />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** A flat row of emotes for the between-games overlay. Always visible there. */
export function EmoteRow({ onSend }: { onSend: (reaction: string) => void }) {
  const fire = useFire(onSend);
  useEffect(() => { preloadEmotes(); }, []);
  return (
    <div
      style={{
        display: 'flex',
        gap: 'clamp(4px,1.4cqmin,8px)',
        flexWrap: 'wrap',
        justifyContent: 'center',
        maxWidth: 'min(92vw,420px)',
      }}
    >
      {REACTIONS.map((r) => (
        <button
          key={r}
          type="button"
          onClick={() => fire(r)}
          aria-label={`React ${r}`}
          style={{
            width: 'clamp(40px,11cqmin,52px)',
            height: 'clamp(40px,11cqmin,52px)',
            borderRadius: '50%',
            border: '1px solid rgba(166,255,46,.2)',
            background: 'rgba(255,255,255,.05)',
            cursor: 'pointer',
            display: 'grid',
            placeItems: 'center',
            touchAction: 'manipulation',
          }}
        >
          <LottieEmote reaction={r} size="clamp(28px,7.5cqmin,40px)" loop />
        </button>
      ))}
    </div>
  );
}
